import Pressable from '@/components/motion/pressable';
import { senderHref, senderRoutes } from '@/lib/sender-routes';
import { resolveSenderTab, type SenderTabId } from '@/lib/sender-navigation';
import { cn } from '@/lib/utils';
import { EllipsisIcon, RadioIcon, SendIcon, UsersIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

type TabDef = {
	id: SenderTabId;
	label: string;
	icon: typeof RadioIcon;
};

const TABS: TabDef[] = [
	{ id: 'now', label: 'Сейчас', icon: RadioIcon },
	{ id: 'send', label: 'Рассылка', icon: SendIcon },
	{ id: 'accounts', label: 'Сессии', icon: UsersIcon },
	{ id: 'more', label: 'Ещё', icon: EllipsisIcon },
];

function tabDestination(id: SenderTabId): string {
	if (id === 'now') return senderRoutes.hub;
	if (id === 'send') return senderHref('/send?mode=batch');
	if (id === 'accounts') return senderRoutes.accounts;
	return senderRoutes.more;
}

interface SenderTabBarProps {
	variant: 'bottom' | 'top';
}

export default function SenderTabBar({ variant }: SenderTabBarProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const from = (location.state as { from?: string } | null)?.from ?? null;
	const active = resolveSenderTab(location.pathname, from);

	return (
		<nav
			aria-label='Разделы'
			className={cn(
				'flex w-full shrink-0 border-border bg-background/95 backdrop-blur-sm',
				variant === 'bottom'
					? 'border-t pb-[env(safe-area-inset-bottom)]'
					: 'border-b overflow-x-auto',
			)}
		>
			{TABS.map(tab => {
				const Icon = tab.icon;
				const selected = active === tab.id;
				return (
					<Pressable
						key={tab.id}
						role='button'
						tabIndex={0}
						className={cn(
							'relative flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 pt-1 text-[13px]',
							selected ? 'text-primary' : 'text-muted-foreground',
						)}
						onClick={() => navigate(tabDestination(tab.id))}
					>
						{selected ? (
							<span className='absolute inset-x-2 top-0 h-0.5 rounded-full bg-primary' aria-hidden />
						) : null}
						<Icon size={20} strokeWidth={selected ? 2.25 : 2} />
						<span className='truncate'>{tab.label}</span>
					</Pressable>
				);
			})}
		</nav>
	);
}
